import { useFeatureFlagEnabled } from '@posthog/react';

import type { FeatureFlagKey } from 'shared/consts';
import { useAuthMe } from 'shared/hooks/auth';
import { resolveFeatureFlag } from 'shared/utils/resolveFeatureFlag';

/** Return shape of {@link useFeatureFlagWithStatus}. */
interface UseFeatureFlagWithStatusReturn {
  /** The resolved flag value — fail-closed `false` until a source answers. */
  isEnabled: boolean;
  /**
   * Whether `isEnabled` is a real answer rather than the fail-closed
   * placeholder — true once the SDK has responded or auth/me (the backend
   * fallback) has loaded. Stays false while both are still pending.
   */
  isReady: boolean;
}

/**
 * Reactive feature-flag check that also reports whether the value has
 * settled. Use it when acting on the fail-closed placeholder would cause a
 * visible wrong state (e.g. stripping a pre-painted theme class) — callers
 * that only branch on the final value should use {@link useFeatureFlag}.
 * SDK-vs-backend precedence lives in {@link resolveFeatureFlag}.
 */
export const useFeatureFlagWithStatus = (flag: FeatureFlagKey): UseFeatureFlagWithStatusReturn => {
  const sdkValue = useFeatureFlagEnabled(flag);
  const { data } = useAuthMe();

  return {
    isEnabled: resolveFeatureFlag(sdkValue, data?.featureAllowance[flag]),
    isReady: sdkValue !== undefined || data !== undefined
  };
};

/**
 * Reactive feature-flag check. Subscribes to PostHog flag updates via the
 * React provider so the component re-renders when the flag value changes.
 * SDK-vs-backend precedence lives in {@link resolveFeatureFlag}.
 */
export const useFeatureFlag = (flag: FeatureFlagKey) => useFeatureFlagWithStatus(flag).isEnabled;
